import { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { useAuth } from '@/lib/auth';
import { useI18n } from '@/lib/i18n';
import { toast } from 'sonner';
import { LogIn, Loader2, Mail, Lock, Eye, EyeOff, ArrowLeft } from 'lucide-react';
import { BookviaLogo } from '@/components/BookviaLogo';
import { AppleSignInButton } from '@/components/AppleSignInButton';
import { isNativeApp, getPlatform } from '@/lib/capacitor';

export default function LoginPage() {
  const { t, language } = useI18n();
  const { unifiedLogin, isAuthenticated, user, googleLogin, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  // ?add=1 → user is adding an extra account from the account-switcher.
  // Don't auto-redirect if already authenticated.
  const isAddingAccount = searchParams.get('add') === '1';

  useEffect(() => {
    if (authLoading) return;
    if (isAddingAccount) return;
    if (isAuthenticated && user) {
      if (user.role === 'business') navigate('/business/dashboard');
      else navigate('/search');
    }
  }, [isAuthenticated, user, navigate, authLoading, isAddingAccount]);

  // Google OAuth callback
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) handleGoogleCallback(sessionId);
  }, [searchParams]);

  const handleGoogleCallback = async (sessionId) => {
    setLoading(true);
    try {
      await googleLogin(sessionId);
      toast.success(language === 'es' ? 'Sesión iniciada con Google' : 'Logged in with Google');
      navigate('/search');
    } catch {
      toast.error(language === 'es' ? 'Error al iniciar con Google' : 'Google login error');
    }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error(language === 'es' ? 'Completa todos los campos' : 'Fill in all fields');
      return;
    }
    setLoading(true);
    try {
      const result = await unifiedLogin(email, password);
      // Admin/staff email + password matched → shortcut to /bv-ctrl/login with
      // the email pre-filled so they only have to complete 2FA. We never issue
      // a token in this path; the admin endpoint handles everything.
      if (result.requires_admin_2fa) {
        toast.info(language === 'es'
          ? 'Cuenta admin detectada. Ingresa tu código 2FA.'
          : 'Admin account detected. Enter your 2FA code.');
        navigate(`/bv-ctrl/login?email=${encodeURIComponent(email)}`);
        return;
      }
      toast.success(language === 'es' ? '¡Bienvenido!' : 'Welcome!');
      if (result.account_type === 'business') navigate('/business/dashboard');
      else navigate('/search');
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail === 'email_not_verified') {
        toast.error(language === 'es' ? 'Verifica tu correo antes de iniciar sesión' : 'Verify your email before logging in');
      } else if (detail === 'subscription_required') {
        toast.error(language === 'es' ? 'Necesitas pagar la suscripción para acceder' : 'You need to pay the subscription to access');
      } else {
        toast.error(language === 'es' ? 'Credenciales incorrectas' : 'Invalid credentials');
      }
    }
    setLoading(false);
  };

  const handleGoogleLogin = () => {
    const redirectUrl = `${window.location.origin}/login`;
    window.location.href = `https://auth.emergentagent.com/google/login?redirect_url=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-start sm:justify-center bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 px-4 pt-6 pb-8 sm:pt-24"
      data-testid="login-page"
    >
      <div className="w-full max-w-md">
        {/* Brand logo — top of page. On desktop the flex-center handles vertical spacing;
            on mobile we anchor to the top so the card sits high and reachable. */}
        <div className="flex justify-center mb-5 sm:mb-6">
          <BookviaLogo variant="light" size="text-3xl" asLink={false} />
        </div>

        <button
          type="button"
          onClick={() => navigate(isAddingAccount ? -1 : '/')}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-[#F05D5E] transition-colors mb-3 group"
          data-testid="login-back-home-btn"
        >
          <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-0.5" />
          {isAddingAccount
            ? (language === 'es' ? 'Volver' : 'Back')
            : (language === 'es' ? 'Volver al inicio' : 'Back to home')}
        </button>
        <Card className="shadow-xl border-0">
          <CardHeader className="text-center pb-2">
            <CardTitle className="text-2xl font-heading font-bold">
              {isAddingAccount
                ? (language === 'es' ? 'Agregar otra cuenta' : 'Add another account')
                : (language === 'es' ? 'Iniciar sesión' : 'Log in')}
            </CardTitle>
            <CardDescription>
              {language === 'es' ? 'Accede a tu cuenta de cliente o negocio' : 'Access your client or business account'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="tu@correo.com"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    className="pl-10 h-12"
                    required
                    autoCapitalize="none"
                    autoCorrect="off"
                    autoComplete="email"
                    data-testid="login-email-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <Label htmlFor="password">{language === 'es' ? 'Contraseña' : 'Password'}</Label>
                  <Link to="/forgot-password" className="text-xs text-[#F05D5E] hover:underline" data-testid="forgot-password-link">
                    {language === 'es' ? '¿Olvidaste tu contraseña?' : 'Forgot password?'}
                  </Link>
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    className="flex h-12 w-full rounded-md border border-input bg-transparent pl-10 pr-10 py-1 text-base shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring md:text-sm"
                    required
                    autoCapitalize="off"
                    autoCorrect="off"
                    autoComplete="current-password"
                    spellCheck="false"
                    data-testid="login-password-input"
                    style={{ textTransform: 'none' }}
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <Button type="submit" className="w-full h-12 btn-coral text-base" disabled={loading} data-testid="login-submit-button">
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : (
                  <><LogIn className="h-5 w-5 mr-2" />{language === 'es' ? 'Iniciar sesión' : 'Log in'}</>
                )}
              </Button>
            </form>

            {/* Divider */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center"><span className="w-full border-t" /></div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-card px-2 text-muted-foreground">{language === 'es' ? 'o continúa con' : 'or continue with'}</span>
              </div>
            </div>

            {/* Sign in with Apple — only renders on native iOS (Guideline 4.8) */}
            <div className="space-y-3">
              <AppleSignInButton mode="login" onSuccess={() => navigate('/')} />

              {/* Google Login — hidden on native iOS to comply with Guideline 4
                  (no external browser during sign-in). Kept on web + Android. */}
              {!(isNativeApp() && getPlatform() === 'ios') && (
                <Button type="button" variant="outline" className="w-full h-12" onClick={handleGoogleLogin} disabled={loading} data-testid="google-login-button">
                  <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                  </svg>
                  Google
                </Button>
              )}
            </div>


            {/* Links */}
            <div className="text-center space-y-3 pt-2">
              <p className="text-sm text-muted-foreground">
                {language === 'es' ? '¿No tienes cuenta?' : "Don't have an account?"}{' '}
                <Link to="/register" className="text-[#F05D5E] font-medium hover:underline" data-testid="register-link">
                  {language === 'es' ? 'Regístrate' : 'Sign up'}
                </Link>
              </p>
              <p className="text-sm text-muted-foreground">
                {language === 'es' ? '¿Tienes un negocio?' : 'Have a business?'}{' '}
                <Link to="/for-business" className="text-[#F05D5E] font-medium hover:underline" data-testid="business-register-link">
                  {language === 'es' ? 'Regístralo aquí' : 'Register it here'}
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
